// ── Homebase engineering-narrative data ───────────────────────────────
//
// One-off, hardcoded dataset backing the standalone /narrative-test page.
// This page is NOT wired to the live API on purpose: it's a client-
// validation artifact for a leadership meeting, and the real timeline /
// highlights path currently OOMs on an org this large. Every figure below
// is pulled from the production `technical-accounting` D1 (org 32,
// GitHub org `pioneerworks`): commit counts + author counts by year, repo
// first/last commits, and the derived roster. Narrative copy is verbatim
// from the approved briefing.

/** Mainline commits per calendar year (org 32, all repos). */
export const COMMITS_BY_YEAR: Record<number, number> = {
  2013: 644,
  2014: 1107,
  2015: 2151,
  2016: 3237,
  2017: 3583,
  2018: 4784,
  2019: 4729,
  2020: 4537,
  2021: 5238,
  2022: 7743,
  2023: 8742,
  2024: 7966,
  2025: 11124,
  2026: 8181,
};

/** Distinct commit authors active in each calendar year. */
export const AUTHORS_BY_YEAR: Record<number, number> = {
  2013: 7,
  2014: 11,
  2015: 17,
  2016: 20,
  2017: 25,
  2018: 36,
  2019: 48,
  2020: 38,
  2021: 66,
  2022: 110,
  2023: 106,
  2024: 107,
  2025: 106,
  2026: 118,
};

/** A narrative chapter — the right-panel copy, shared across its snapshots. */
export interface NarrativeEra {
  id: string;
  chapter: string;
  range: string;
  title: string;
  /** Verbatim briefing copy. */
  copy: string;
}

export const ERAS: NarrativeEra[] = [
  {
    id: 'founding',
    chapter: 'Chapter 1',
    range: '2013–2017',
    title: 'Founding & the monolith',
    copy:
      'The story starts with the Homebase1 repo (April 2013), a Ruby-on-Rails + React monolith that is still, today, ~90% of all engineering activity (66.5k of 73.7k commits, 307 distinct authors over its life). A small founding team (~7–8 committers/yr). A native iOS app (homebase-ios, Objective-C/UIKit) was started in 2014 — but its last commit is January 2017. It’s effectively abandoned (only 4 authors, 243 commits ever).',
  },
  {
    id: 'scaleup',
    chapter: 'Chapter 2',
    range: '2018–2021',
    title: 'Scale-up',
    copy:
      'Headcount and output ramp sharply: active committers grow from ~36 (2018) to 66 (2021), then 110+ from 2022 onward. This is the “hit product-market-fit, scale the team” phase — the single biggest hiring wave lands in 2021 (~37 new contributors).',
  },
  {
    id: 'modernization',
    chapter: 'Chapter 3',
    range: '2022',
    title: 'Peak throughput & modernization',
    copy:
      'The Android app is rebuilt on a modern stack — Jetpack Compose, Hilt DI, Coroutines, a real design system with convention plugins and screenshot tests. Feature-flagged experimentation via Statsig appears. The fintech expansion (Cash Out, banking, Plaid/KYC, VGS) matures.',
  },
  {
    id: 'extraction',
    chapter: 'Chapter 4',
    range: '2023',
    title: 'First service extraction',
    copy:
      'A standalone notifications microservice (Ruby/gRPC via gruf, multi-provider SMS/email/push) is carved out of the monolith — the first real step toward decomposition.',
  },
  {
    id: 'ai',
    chapter: 'Chapter 5',
    range: '2025–2026',
    title: 'AI-assisted development',
    copy:
      'Commit volume peaks (11k in 2025). Automated contributors appear in the history: Devin (Oct 2025) and GitHub Copilot (Mar 2026), alongside long-running Dependabot/CircleCI automation.',
  },
];

export function eraById(id: string): NarrativeEra {
  return ERAS.find((e) => e.id === id) ?? ERAS[0]!;
}

export interface Metric {
  value: string;
  label: string;
}

/** A single point on the scrubber — the center card's per-moment content. */
export interface NarrativeSnapshot {
  id: string;
  /** YYYY-MM-DD — drives the scrubber tick + date labels. */
  date: string;
  /** Card year heading, e.g. "2016" or "2025–2026". */
  yearLabel: string;
  eraId: string;
  /** Short "what happened" headline for the card. */
  cardTitle: string;
  metrics: Metric[];
  events: string[];
  /** Year(s) lit up in the commit-activity strip. */
  highlightYears: number[];
}

// 10 snapshots: 4 founding · 3 scale-up · 1 modernization · 1 extraction · 1 AI.
export const SNAPSHOTS: NarrativeSnapshot[] = [
  {
    id: 's-2013',
    date: '2013-04-25',
    yearLabel: '2013',
    eraId: 'founding',
    cardTitle: 'Homebase1 is born',
    metrics: [
      { value: '644', label: 'commits in 2013' },
      { value: '7', label: 'contributors' },
      { value: '1', label: 'active repo' },
      { value: 'Rails + React', label: 'stack' },
    ],
    events: [
      'The Homebase1 monolith is created (April 2013).',
      'The Ruby-on-Rails + React codebase that still carries ~90% of activity today.',
    ],
    highlightYears: [2013],
  },
  {
    id: 's-2014',
    date: '2014-06-02',
    yearLabel: '2014',
    eraId: 'founding',
    cardTitle: 'Mobile begins',
    metrics: [
      { value: '1,107', label: 'commits in 2014' },
      { value: '11', label: 'contributors' },
      { value: '3', label: 'active repos' },
      { value: 'Rails · iOS · Android', label: 'stack' },
    ],
    events: [
      'Native iOS app (homebase-ios, Objective-C/UIKit) is started.',
      'The mobile-android repo opens alongside it.',
    ],
    highlightYears: [2014],
  },
  {
    id: 's-2016',
    date: '2016-06-01',
    yearLabel: '2016',
    eraId: 'founding',
    cardTitle: 'The monolith scales',
    metrics: [
      { value: '3,237', label: 'commits in 2016' },
      { value: '20', label: 'contributors' },
      { value: '3', label: 'active repos' },
      { value: 'Rails + React', label: 'stack' },
    ],
    events: [
      'Product breadth keeps growing on the Rails monolith.',
      'Still a small founding team (~20 committers/yr).',
    ],
    highlightYears: [2016],
  },
  {
    id: 's-2017',
    date: '2017-01-04',
    yearLabel: '2017',
    eraId: 'founding',
    cardTitle: 'Native iOS is abandoned',
    metrics: [
      { value: '3,583', label: 'commits in 2017' },
      { value: '25', label: 'contributors' },
      { value: '2', label: 'active repos' },
      { value: 'Rails + React', label: 'stack' },
    ],
    events: [
      'homebase-ios makes its last commit — January 2017.',
      'Only 4 authors and 243 commits ever; effectively abandoned.',
    ],
    highlightYears: [2017],
  },
  {
    id: 's-2018',
    date: '2018-06-01',
    yearLabel: '2018',
    eraId: 'scaleup',
    cardTitle: 'Product-market fit',
    metrics: [
      { value: '4,784', label: 'commits in 2018' },
      { value: '36', label: 'contributors' },
      { value: '2', label: 'active repos' },
      { value: 'Rails · Android', label: 'stack' },
    ],
    events: [
      'Active committers reach ~36 as output ramps.',
      'The team starts scaling behind a working product.',
    ],
    highlightYears: [2018],
  },
  {
    id: 's-2019',
    date: '2019-06-01',
    yearLabel: '2019',
    eraId: 'scaleup',
    cardTitle: 'The team scales',
    metrics: [
      { value: '4,729', label: 'commits in 2019' },
      { value: '48', label: 'contributors' },
      { value: '2', label: 'active repos' },
      { value: 'Rails · Android', label: 'stack' },
    ],
    events: [
      '48 active committers — headcount climbing steadily.',
      'Engineering investment broadens across the product.',
    ],
    highlightYears: [2019],
  },
  {
    id: 's-2021',
    date: '2021-06-01',
    yearLabel: '2021',
    eraId: 'scaleup',
    cardTitle: 'Biggest hiring wave',
    metrics: [
      { value: '5,238', label: 'commits in 2021' },
      { value: '66', label: 'contributors' },
      { value: '2', label: 'active repos' },
      { value: 'Rails · Android', label: 'stack' },
    ],
    events: [
      '66 active committers.',
      'The single biggest hiring wave — ~37 new contributors join in 2021.',
    ],
    highlightYears: [2021],
  },
  {
    id: 's-2022',
    date: '2022-06-01',
    yearLabel: '2022',
    eraId: 'modernization',
    cardTitle: 'Android rebuilt · fintech matures',
    metrics: [
      { value: '7,743', label: 'commits in 2022' },
      { value: '110', label: 'contributors' },
      { value: '2', label: 'active repos' },
      { value: 'Compose · Hilt', label: 'stack' },
    ],
    events: [
      'Android rebuilt: Jetpack Compose, Hilt DI, Coroutines, a real design system + screenshot tests.',
      'Feature-flagged experimentation via Statsig appears.',
      'Fintech (Cash Out, banking, Plaid/KYC, VGS) matures.',
    ],
    highlightYears: [2022],
  },
  {
    id: 's-2023',
    date: '2023-02-01',
    yearLabel: '2023',
    eraId: 'extraction',
    cardTitle: 'notifications carved out',
    metrics: [
      { value: '8,742', label: 'commits in 2023' },
      { value: '106', label: 'contributors' },
      { value: '3', label: 'active repos' },
      { value: 'Ruby/gRPC (gruf)', label: 'new service' },
    ],
    events: [
      'A standalone notifications microservice is carved out of the monolith.',
      'Ruby/gRPC via gruf · multi-provider SMS/email/push.',
      'The first real step toward decomposition.',
    ],
    highlightYears: [2023],
  },
  {
    id: 's-2026',
    date: '2026-03-26',
    yearLabel: '2025–2026',
    eraId: 'ai',
    cardTitle: 'AI-assisted development',
    metrics: [
      { value: '11,124', label: 'commits (2025 peak)' },
      { value: '118', label: 'contributors (2026)' },
      { value: '3', label: 'active repos' },
      { value: '+ Devin · Copilot', label: 'automation' },
    ],
    events: [
      'Commit volume peaks — 11k in 2025.',
      'Devin (Oct 2025) and GitHub Copilot (Mar 2026) appear in the history.',
      'Alongside long-running Dependabot / CircleCI automation.',
    ],
    highlightYears: [2025, 2026],
  },
];

// ── Left pull-out drawer: Risks + What's already being done ────────────

export interface RiskGroup {
  heading: string;
  items: string[];
}

export const RISK_GROUPS: RiskGroup[] = [
  {
    heading: 'Resourcing',
    items: [
      'Severe key-person concentration — one engineer ≈ 29% of all measured contribution; the top 5 ≈ 46%. A real bus-factor risk.',
      '~53% lifetime attrition (124 of 235 contributors have departed), including several heavy hitters.',
      'Institutional knowledge sits in undocumented domains — the Homebase domain model, timecard/labor-compliance, the homebase_service gem.',
    ],
  },
  {
    heading: 'Maintenance',
    items: [
      'A 13-year-old Rails monolith carries ~90% of the codebase — a large, high-coupling maintenance surface.',
      'Legacy iOS is a blind spot — the onboarded repo has been dead since 2017; the live iOS app (if any) isn’t onboarded yet.',
      'A heavy compliance surface (payroll, wages, banking, KYC, PCI card data) can’t be allowed to rot.',
    ],
  },
  {
    heading: 'Architecture',
    items: [
      'Monolith-centric — only one service (notifications) has been extracted so far.',
      'Divergent mobile maturity — modern Android vs. legacy/unclear iOS.',
      'Broad third-party coupling — ~27 external services; each POS / payroll partner is its own integration point.',
    ],
  },
];

export const PROGRESS_ITEMS: string[] = [
  'Android modernization — Jetpack Compose, modular architecture, a design system, and screenshot tests.',
  'First service extraction — notifications carved out (Ruby/gRPC) as a decomposition beachhead.',
  'Experiment-driven delivery via Statsig feature flags.',
  'AI-assisted development adopted — Devin (2025) and GitHub Copilot (2026).',
  'Deliberate fintech build-out with PCI isolation (VGS Vault) and KYC via Plaid.',
];
